import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, Spinner, Form, InputGroup } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { auditLogService, setAuthToken } from '../services/apiService';
import StackedTable from './StackedTable';
import { ErrorState, LoadingState, serverMessage } from './PageState';
import { EMPTY, formatDateTime, formatNumber, humanizeEnum } from '../utils/format';

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 400;
const EMPTY_FILTERS = { action: '', source: '', from: '', to: '' };

// Words in action and source names that keep their own capitals once humanised
const PROPER_WORDS = { API: 'API', IP: 'IP', MCP: 'MCP', SCIM: 'SCIM', OUTLOOK: 'Outlook' };

/** "UPDATE_MEETING_ROOM_OUTLOOK_EVENT" reads as "Update meeting room Outlook event", "PUBLIC_API" as "Public API". */
const enumLabel = (value) => humanizeEnum(value, EMPTY)
  .split(' ')
  .map((word) => PROPER_WORDS[word.toUpperCase()] || word)
  .join(' ');

/**
 * Company audit log tab: server-side search, action/source/date filters and a user filter
 * set by clicking a name in the table. Filtering happens in the API so it covers the whole
 * log, not just the page of 100 currently loaded.
 */
const CompanyAuditLog = ({ companyId }) => {
  const { instance, accounts } = useMsal();
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ actions: [], sources: [] });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [user, setUser] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const requestId = useRef(0);

  const getAccessToken = useCallback(async () => {
    const response = await instance.acquireTokenSilent({
      scopes: ['api://7e9ba977-33b0-4e9c-ae75-55f65da317bd/access'],
      account: accounts[0],
    });
    return response.accessToken;
  }, [instance, accounts]);

  // Debounce the search box so typing doesn't fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== search) {
        setSearch(next);
        setPage(0);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setAuthToken(await getAccessToken());
        const response = await auditLogService.getAuditLogFilters(companyId);
        setFilterOptions(response.data);
      } catch (err) {
        // The dropdowns just stay empty; search and dates still work
        console.error(err);
      }
    };
    loadFilterOptions();
  }, [companyId, getAccessToken]);

  useEffect(() => {
    // Ignore responses that arrive after a newer request was started
    const thisRequest = ++requestId.current;
    const loadLogs = async () => {
      try {
        setLoading(true);
        setError(null);
        setAuthToken(await getAccessToken());
        const response = await auditLogService.getAuditLogsByCompany(companyId, {
          q: search || undefined,
          action: filters.action || undefined,
          source: filters.source || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
          userId: user?.id,
          page,
          size: PAGE_SIZE,
        });
        if (thisRequest !== requestId.current) return;
        setLogs(response.data);
      } catch (err) {
        if (thisRequest !== requestId.current) return;
        setError(serverMessage(err, 'Could not load the audit log.'));
        console.error(err);
      } finally {
        if (thisRequest === requestId.current) setLoading(false);
      }
    };
    loadLogs();
  }, [companyId, getAccessToken, search, filters, user, page, reloadKey]);

  const handleFilterChange = (key) => (e) => {
    const { value } = e.target;
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const filterByUser = (id, name) => {
    setUser({ id, name: name || id });
    setPage(0);
  };

  const clearUser = () => {
    setUser(null);
    setPage(0);
  };

  const clearAll = () => {
    setSearchInput('');
    setSearch('');
    setFilters(EMPTY_FILTERS);
    setUser(null);
    setPage(0);
  };

  const isFiltered = Boolean(search || user || Object.values(filters).some(Boolean));

  const renderUser = (id, name) => {
    if (!id) return name || EMPTY;
    if (user?.id === id) return <span className="fw-semibold">{name || id}</span>;
    return (
      <Button
        variant="link"
        size="sm"
        className="p-0 align-baseline text-start"
        title="Show only entries involving this user"
        onClick={() => filterByUser(id, name)}
      >
        {name || id}
      </Button>
    );
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-light">
        <h5 className="mb-3">Audit log</h5>
        <div className="d-flex flex-wrap align-items-end gap-3 filter-bar">
          <Form.Group style={{ flex: '1 1 260px', maxWidth: 420 }} className="filter-bar-wide">
            <Form.Label className="mb-1 small text-muted">Search</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                type="search"
                placeholder="Summary, user name or ID, action..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {loading && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">Loading</span>
                  </Spinner>
                </InputGroup.Text>
              )}
            </InputGroup>
          </Form.Group>

          <Form.Group>
            <Form.Label className="mb-1 small text-muted">Action</Form.Label>
            <Form.Select size="sm" value={filters.action} onChange={handleFilterChange('action')}>
              <option value="">All actions</option>
              {filterOptions.actions.map((action) => (
                <option key={action} value={action}>{enumLabel(action)}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label className="mb-1 small text-muted">Source</Form.Label>
            <Form.Select size="sm" value={filters.source} onChange={handleFilterChange('source')}>
              <option value="">All sources</option>
              {filterOptions.sources.map((source) => (
                <option key={source} value={source}>{enumLabel(source)}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label className="mb-1 small text-muted">From</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={filters.from}
              max={filters.to || undefined}
              onChange={handleFilterChange('from')}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="mb-1 small text-muted">To</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={filters.to}
              min={filters.from || undefined}
              onChange={handleFilterChange('to')}
            />
          </Form.Group>

          {isFiltered && (
            <Button variant="outline-secondary" size="sm" onClick={clearAll}>
              Clear filters
            </Button>
          )}
        </div>

        {user && (
          <div className="mt-2 small">
            Showing entries where <span className="fw-semibold">{user.name}</span> is the actioning or affected user.{' '}
            <Button variant="link" size="sm" className="p-0 align-baseline" onClick={clearUser}>
              Show all users
            </Button>
          </div>
        )}
      </Card.Header>

      <Card.Body className="p-0">
        {error && (
          <ErrorState message={error} onRetry={() => setReloadKey((key) => key + 1)} className="m-3" />
        )}

        {loading && logs.length === 0 ? (
          <LoadingState label="Loading the audit log" />
        ) : error ? null : logs.length === 0 ? (
          <div className="p-4 text-center text-muted">
            {isFiltered ? 'No entries match these filters' : 'No audit log entries for this company yet'}
          </div>
        ) : (
          <StackedTable responsive className="mb-0" style={{ opacity: loading ? 0.6 : 1 }}>
            <thead className="table-light">
              <tr>
                <th>Date and time</th>
                <th>Action</th>
                <th className="d-none d-md-table-cell">Source</th>
                <th>Actioning user</th>
                <th>Affected user</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.createdAt)}</td>
                  <td>{enumLabel(log.action)}</td>
                  <td className="small text-muted d-none d-md-table-cell">{log.source ? enumLabel(log.source) : EMPTY}</td>
                  <td>{renderUser(log.actioningUserId, log.actioningUserName)}</td>
                  <td>{renderUser(log.affectedUserId, log.affectedUserName)}</td>
                  <td className="stack-block">{log.summary || EMPTY}</td>
                </tr>
              ))}
            </tbody>
          </StackedTable>
        )}

        {!error && (logs.length > 0 || page > 0) && (
          <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 0 || loading}
            >
              Previous {PAGE_SIZE}
            </Button>
            <span className="text-muted">
              {logs.length > 0
                ? `${formatNumber(page * PAGE_SIZE + 1)} to ${formatNumber(page * PAGE_SIZE + logs.length)}`
                : 'No more entries'}
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={logs.length < PAGE_SIZE || loading}
            >
              Next {PAGE_SIZE}
            </Button>
          </Card.Footer>
        )}
      </Card.Body>
    </Card>
  );
};

export default CompanyAuditLog;
